/**
 * Mode of Travel Section Component
 * Displays travel mode options with icons and description
 * Follows Single Responsibility Principle
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  typographyColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  brandColors,
} from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { TravelModeChips } from './TravelModeChips';
import { ShowMoreTextButton } from '@/components/ui/show-more-text-button';
import { useTextOverflow } from '@/hooks/useTextOverflow';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface TravelMode {
  id: string;
  icon: string;
  label: string;
}

export interface ModeOfTravelSectionProps {
  modes: TravelMode[];
  description: string;
  getIcon: (iconName: string) => React.ReactNode;
  className?: string;
}

export const ModeOfTravelSection: React.FC<ModeOfTravelSectionProps> = ({
  modes,
  description,
  getIcon,
  className,
}) => {
  const [activeMode, setActiveMode] = useState<string>(
    modes && modes.length > 0 ? modes[0].id : ''
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Parse description to extract individual mode descriptions
  // Format: "Flight: description (cost)" or "By Flight: description (cost)"
  // Must be computed before early return to use in hooks
  const modeDescriptions = useMemo(() => {
    if (!modes || modes.length === 0) return {};

    return description.split('\n\n').reduce(
      (acc, paragraph) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return acc;

        // Try to match mode title at the start (e.g., "Flight:", "By Flight:", "Train:", etc.)
        const modeMatch = trimmed.match(/^(?:By\s+)?([^:]+):\s*(.+)$/);
        if (modeMatch) {
          const modeTitle = modeMatch[1].trim();
          let modeDesc = modeMatch[2].trim();

          // Remove cost information in parentheses at the end
          modeDesc = modeDesc.replace(/\s*\([^)]+\)\s*$/, '');

          // Find matching mode by label (try exact match, then case-insensitive, then partial match)
          const matchingMode =
            modes.find(
              m => m.label.toLowerCase() === modeTitle.toLowerCase()
            ) ||
            modes.find(m =>
              m.label.toLowerCase().includes(modeTitle.toLowerCase())
            ) ||
            modes.find(m =>
              modeTitle.toLowerCase().includes(m.label.toLowerCase())
            );

          if (matchingMode) {
            acc[matchingMode.id] = modeDesc;
            console.log('🔍 [ModeOfTravel] Matched mode:', {
              modeTitle,
              modeLabel: matchingMode.label,
              modeId: matchingMode.id,
              description: modeDesc,
            });
          } else {
            // If no match, add to first mode or a default key
            const fallbackId = modes[0]?.id || 'default';
            acc[fallbackId] = (acc[fallbackId] || '') + trimmed;
            console.log('⚠️ [ModeOfTravel] No match found:', {
              modeTitle,
              availableLabels: modes.map(m => m.label),
              fallbackId,
            });
          }
        } else {
          // If no mode title found, add to active mode or first mode
          const targetMode = activeMode || modes[0]?.id || 'default';
          acc[targetMode] =
            (acc[targetMode] || '') + (acc[targetMode] ? ' ' : '') + trimmed;
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [description, modes, activeMode]);

  // Update activeMode when modes change
  useEffect(() => {
    if (modes && modes.length > 0 && !modes.find(m => m.id === activeMode)) {
      setActiveMode(modes[0].id);
    }
  }, [modes, activeMode]);

  // Get description for active mode, fallback to first mode or full description
  // Must be computed before early return to use in hooks
  const activeDescription =
    modes && modes.length > 0
      ? modeDescriptions[activeMode] ||
        modeDescriptions[modes[0]?.id] ||
        description
      : description;

  // Check if text exceeds 3 lines - must be called before early return
  const { textRef, exceedsLines } = useTextOverflow<HTMLParagraphElement>(
    3,
    activeDescription
  );

  // Debug log for active mode changes - must be before early return
  useEffect(() => {
    if (!modes || modes.length === 0) return;

    console.log('🔍 [ModeOfTravel] Active mode changed:', {
      activeMode,
      hasDescription: !!modeDescriptions[activeMode],
      description: modeDescriptions[activeMode] || 'Using fallback',
      allModeDescriptions: Object.keys(modeDescriptions),
    });
  }, [activeMode, modeDescriptions, modes]);

  if (!modes || modes.length === 0) {
    return null;
  }

  console.log('🔍 [ModeOfTravel] Parsed descriptions:', {
    activeMode,
    modeDescriptions,
    availableModes: modes.map(m => ({ id: m.id, label: m.label })),
  });

  // Handle mode change with logging
  const handleModeChange = (modeId: string) => {
    console.log('🔄 [ModeOfTravel] Mode change requested:', {
      from: activeMode,
      to: modeId,
      availableModes: modes.map(m => m.id),
    });
    setActiveMode(modeId);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      <SectionHeader title='Mode of Travel' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? spacing[2] : spacing[3], // Smaller gap on mobile
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
        }}
      >
        {/* Mode Chips/Tabs */}
        <TravelModeChips
          modes={modes}
          activeMode={activeMode}
          onModeChange={handleModeChange}
          getIcon={getIcon}
        />

        {/* Description */}
        <div
          style={{
            backgroundColor: '#fcfcfc',
            border: `1px solid ${brandColors.offset}`,
            borderRadius: borders.radius.xl,
            padding: `${spacing[2]}px ${spacing[3]}px`, // 8px vertical, 12px horizontal
          }}
        >
          <div
            style={{
              padding: `${spacing[1]}px ${spacing[3]}px`, // 4px vertical, 12px horizontal
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[1],
            }}
          >
            <motion.p
              ref={textRef}
              animate={{
                WebkitLineClamp: isExpanded ? 'none' : 3,
              }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.sm,
                fontWeight: typography.weight.regular,
                color: typographyColors.text,
                lineHeight: typography.lineHeight.normal,
                margin: 0,
                whiteSpace: 'pre-wrap',
                display: '-webkit-box',
                WebkitLineClamp: isExpanded ? 'none' : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {activeDescription}
            </motion.p>
            {exceedsLines && (
              <ShowMoreTextButton
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
